import {
  RouteLocationNormalizedGeneric,
  RouteRecordRaw,
  NavigationGuardNext,
} from 'vue-router';
import { useNewBookingStore } from 'src/stores/newBookingStore';
import { useAuthStore } from 'src/stores/authStore';
import { usePropertyStore } from 'src/stores/propertyStore';
import { usePreCheckInStore } from 'src/stores/preCheckInStore';

const routes: RouteRecordRaw[] = [
  {
    path: '/Book/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const ps = usePropertyStore();
      const pcStore = useNewBookingStore();
      pcStore.getBookConfig(ps.propertyId).finally(() => {
        next();
      });
    },
    children: [
      {
        name: 'Book',
        path: '',
        component: () => import('pages/Book/SearchPage.vue'),
      },
      {
        name: 'Rooms-And-Rates',
        path: '/Book/:propertyCode/Rooms-And-Rates',
        component: () => import('pages/Book/RoomsAndRatesPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const newBookingStore = useNewBookingStore();
          if (!newBookingStore.canSearch) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
      {
        name: 'Checkout',
        path: '/Book/:propertyCode/Checkout/:confirmationToken?',
        component: () => import('pages/Book/CheckoutPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const newBookingStore = useNewBookingStore();
          const confirmationToken = to.params['confirmationToken'];
          if (!newBookingStore.hasReservationItems && !confirmationToken) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
      {
        name: 'Confirmation',
        path: '/Book/:propertyCode/Confirmation/:confirmationToken',
        component: () => import('pages/Book/ConfirmationPage.vue'),
        beforeEnter: (to: RouteLocationNormalizedGeneric) => {
          const confirmationToken = to.params['confirmationToken'];
          if (!confirmationToken) {
            const str = '/' + to.name.toString();
            return {
              path: to.path.replace(str, ''),
              query: to.query,
              hash: to.hash,
            };
          }
          return true;
        },
      },
    ],
  },
  {
    path: '/PreCheckIn/:propertyCode/:referenceNumber/:code',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const ps = usePropertyStore();
      const pcStore = usePreCheckInStore();
      pcStore.getPreCheckInConfig(ps.propertyId).finally(() => {
        next();
      });
    },
    children: [
      {
        name: 'PreCheckIn',
        path: '',
        component: () => import('pages/PreCheckIn/PreCheckInPage.vue'),
      },
    ],
  },
  {
    path: '/BookGroup/:propertyCode',
    component: () => import('layouts/PropertyGroupLayout.vue'),
    children: [
      {
        name: 'BookGroup',
        path: '',
        component: () =>
          import('pages/PropertyGroup/PropertyGroupSearchPage.vue'),
      },
    ],
  },
  {
    path: '/Account/ForgotPassword/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'ForgotPassword',
        path: '',
        component: () => import('pages/Account/ForgotPasswordPage.vue'),
      },
    ],
  },
  {
    path: '/Account/ResetPassword/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'ResetPassword',
        path: '',
        component: () => import('pages/Account/ResetPasswordPage.vue'),
      },
    ],
  },
  {
    path: '/Account/ConfirmEmail/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'ConfirmEmail',
        path: '',
        component: () => import('pages/Account/ConfirmEmailPage.vue'),
      },
    ],
  },
  {
    path: '/Account/Register/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    children: [
      {
        name: 'Register',
        path: '',
        component: () => import('pages/Account/RegisterPage.vue'),
      },
    ],
  },
  {
    path: '/ManageAccount/:propertyCode',
    component: () => import('layouts/PropertyLayout.vue'),
    beforeEnter: async (
      to: RouteLocationNormalizedGeneric,
      from: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const authStore = useAuthStore();
      const ps = usePropertyStore();
      await authStore.getUser(ps.propertyId);

      if (authStore.isUserLoggedIn) {
        next();
      } else {
        // redirect them to book
        next({
          name: 'Book',
          params: { propertyCode: to.params.propertyCode },
        });
      }
    },
    children: [
      {
        name: 'ManageAccount',
        path: '',
        component: () => import('pages/Account/ManageAccountPage.vue'),
      },
    ],
  },
  {
    path: '/Unauthorised',
    children: [
      {
        name: 'Unauthorised',
        path: '',
        component: () => import('pages/UnauthorisedPage.vue'),
      },
    ],
  },
  {
    path: '/Error',
    children: [
      {
        name: 'Error',
        path: '',
        component: () => import('pages/ErrorNotFound.vue'),
      },
    ],
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue'),
  },
];

export default routes;
